import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  Checkbox,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ColumnConfig, ExportColumnOption, ExportTableProps } from "./types";
import { exportToCSV } from "./utils";

export const ExportTable = React.forwardRef<HTMLDivElement, ExportTableProps<any>>(
  (
    {
      title,
      titleTooltip,
      columns,
      data,
      exportTitle = title,
      showExport = true,
      onExport,
      onDetailExport,
      detailExportLoading = false,
      exportColumnOptions,
      hoverable = true,
      stickyHeader = true,
    },
    ref
  ) => {
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");

    // Column picker dialog state
    const [columnPickerOpen, setColumnPickerOpen] = useState(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<Set<string>>(new Set());
    const [exportMode, setExportMode] = useState<"summary" | "detail">("summary");

    const handleSelectRow = (rowId: string) => {
      const newSelected = new Set(selectedRows);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      setSelectedRows(newSelected);
    };

    const handleSort = (columnKey: string) => {
      if (sortColumn === columnKey) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumn(columnKey);
        setSortDirection("asc");
      }
    };

    // Build the list of available columns for the picker
    const summaryColumnOptions: ExportColumnOption[] = useMemo(
      () => columns.map((c) => ({ key: String(c.key), label: c.label })),
      [columns]
    );

    const detailColumnOptions: ExportColumnOption[] = useMemo(
      () => exportColumnOptions ?? summaryColumnOptions,
      [exportColumnOptions, summaryColumnOptions]
    );

    // Open column picker for summary export
    const handleSummaryExportClick = () => {
      setExportMode("summary");
      const allKeys = new Set(summaryColumnOptions.map((c) => c.key));
      setSelectedExportColumns(allKeys);
      setColumnPickerOpen(true);
    };

    // Open column picker for detail export
    const handleDetailExportClick = () => {
      setExportMode("detail");
      const allKeys = new Set(detailColumnOptions.map((c) => c.key));
      setSelectedExportColumns(allKeys);
      setColumnPickerOpen(true);
    };

    const handleColumnToggle = (key: string) => {
      const next = new Set(selectedExportColumns);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      setSelectedExportColumns(next);
    };

    const handleSelectAllColumns = () => {
      const opts = exportMode === "detail" ? detailColumnOptions : summaryColumnOptions;
      if (selectedExportColumns.size === opts.length) {
        setSelectedExportColumns(new Set());
      } else {
        setSelectedExportColumns(new Set(opts.map((c) => c.key)));
      }
    };

    const handleColumnPickerConfirm = () => {
      if (exportMode === "summary") {
        // Filter table columns to selected ones
        const exportCols = columns
          .filter((col) => selectedExportColumns.has(String(col.key)))
          .map((col) => ({ key: col.key, label: col.label }));
        const exportData = searchQuery.trim() ? filteredData : data;
        exportToCSV(exportData, exportCols, `${exportTitle}-summary.csv`);
        if (onExport) onExport("csv");
      } else {
        // Detail export with selected columns
        const selectedData = filteredData.filter((row) => selectedRows.has(row.id));
        const chosenColumns = detailColumnOptions.filter((c) => selectedExportColumns.has(c.key));
        if (onDetailExport) {
          onDetailExport(selectedData, chosenColumns);
        } else {
          // Fallback: export selected rows with table columns
          const exportCols = columns
            .filter((col) => selectedExportColumns.has(String(col.key)))
            .map((col) => ({ key: col.key, label: col.label }));
          exportToCSV(selectedData, exportCols, `${exportTitle}-detail.csv`);
          if (onExport) onExport("csv");
        }
      }
      setColumnPickerOpen(false);
    };
    const sortedData = useMemo(() => {
      if (!sortColumn) return data;

      const sorted = [...data].sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        // Handle null/undefined
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === "asc" ? 1 : -1;
        if (bValue == null) return sortDirection === "asc" ? -1 : 1;

        // Compare values
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }

        // String comparison
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return sortDirection === "asc" ? comparison : -comparison;
      });

      return sorted;
    }, [data, sortColumn, sortDirection]);

    // Memoized filtered data based on search query
    const filteredData = useMemo(() => {
      if (!searchQuery.trim()) return sortedData;

      const lowerQuery = searchQuery.toLowerCase();
      return sortedData.filter((row) => {
        return columns.some((column) => {
          const value = row[column.key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(lowerQuery);
        });
      });
    }, [sortedData, searchQuery, columns]);

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const allIds = new Set(filteredData.map((row) => row.id));
        setSelectedRows(allIds);
      } else {
        setSelectedRows(new Set());
      }
    };

    const isAllSelected = filteredData.length > 0 && selectedRows.size === filteredData.length;
    const isSomeSelected = selectedRows.size > 0 && selectedRows.size < filteredData.length;

    // Calculate min/max ranges for columns with dynamic chip coloring
    const columnRanges = useMemo(() => {
      const ranges: Record<string, { min: number; max: number }> = {};
      
      columns.forEach((column) => {
        if (column.type === "chip" && column.chipColorMode === "dynamic") {
          const values = data
            .map((row) => row[column.key])
            .filter((v): v is number => typeof v === "number");
          
          if (values.length > 0) {
            ranges[column.key as string] = {
              min: Math.min(...values),
              max: Math.max(...values),
            };
          }
        }
      });
      
      return ranges;
    }, [data, columns]);

    // Get dynamic chip color based on value position in range
    const getDynamicChipColor = (
      value: number,
      range: { min: number; max: number }
    ): "error" | "warning" | "success" => {
      if (range.max === range.min) return "success";
      
      const normalized = (value - range.min) / (range.max - range.min);
      
      // Top third = error (red), middle third = warning (orange), bottom third = success (green)
      if (normalized >= 0.67) return "error";
      if (normalized >= 0.33) return "warning";
      return "success";
    };

    const renderCell = (column: ColumnConfig<any>, row: any) => {
      const value = row[column.key];

      // Custom render function
      if (column.render) {
        return column.render(value, row);
      }

      // Chip type
      if (column.type === "chip") {
        let chipColor: "primary" | "secondary" | "success" | "error" | "warning" | "info" | "default" = "default";
        
        if (column.chipColorMode === "dynamic" && typeof value === "number") {
          const range = columnRanges[column.key as string];
          if (range) {
            chipColor = getDynamicChipColor(value, range);
          }
        } else if (column.chipColor) {
          chipColor = column.chipColor(value, row);
        }
        
        return (
          <Chip
            label={typeof value === "number" ? value.toLocaleString() : value}
            size="small"
            variant={column.chipVariant || "outlined"}
            color={chipColor}
          />
        );
      }

      // Number type with locale formatting
      if (column.type === "number" && typeof value === "number") {
        return value.toLocaleString();
      }

      // Default text type
      return value || "-";
    };

    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {title}
            </Typography>
            {titleTooltip && (
              <Tooltip title={titleTooltip} arrow placement="top">
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
              </Tooltip>
            )}
          </Box>
          {showExport && data.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {selectedRows.size > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.5,
                    border: 2,
                    borderColor: "primary.main",
                    borderRadius: 1,
                    backgroundColor: "action.hover",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                    ({selectedRows.size})
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleDetailExportClick}
                    disabled={detailExportLoading}
                    sx={{
                      "&:hover": {
                        backgroundColor: "primary.light",
                        opacity: 0.8,
                      },
                    }}
                    title={detailExportLoading ? "Exporting..." : `Export ${selectedRows.size} selected row${selectedRows.size !== 1 ? "s" : ""} as CSV`}
                  >
                    {detailExportLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              )}
              <IconButton
                size="small"
                onClick={handleSummaryExportClick}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                title="Export table data as CSV"
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            placeholder="Search table..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
              },
            }}
          />
        </Box>

        <TableContainer
          className="table-scroll"
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader={stickyHeader}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align || "left"}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "background.paper",
                    }}
                    sortDirection={sortColumn === String(column.key) ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortColumn === String(column.key)}
                      direction={sortDirection}
                      onClick={() => handleSort(String(column.key))}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    hover={hoverable}
                    selected={selectedRows.has(row.id)}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.has(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell
                        key={`${row.id || index}-${String(column.key)}`}
                        align={column.align || "left"}
                        sx={{
                          maxWidth: column.maxWidth,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          column.type !== "chip" && column.type !== "custom"
                            ? String(row[column.key] || "")
                            : undefined
                        }
                      >
                        {renderCell(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary" variant="body2">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Column Picker Dialog */}
        <Dialog
          open={columnPickerOpen}
          onClose={() => setColumnPickerOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Select Columns to Export
          </DialogTitle>
          <DialogContent dividers>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    selectedExportColumns.size ===
                    (exportMode === "detail" ? detailColumnOptions : summaryColumnOptions).length
                  }
                  indeterminate={
                    selectedExportColumns.size > 0 &&
                    selectedExportColumns.size <
                      (exportMode === "detail" ? detailColumnOptions : summaryColumnOptions).length
                  }
                  onChange={handleSelectAllColumns}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Select All
                </Typography>
              }
              sx={{ width: "100%" }}
            />
            <Divider sx={{ mb: 1 }} />
            <FormGroup>
              {(exportMode === "detail" ? detailColumnOptions : summaryColumnOptions).map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={selectedExportColumns.has(col.key)}
                      onChange={() => handleColumnToggle(col.key)}
                      size="small"
                    />
                  }
                  label={col.label}
                />
              ))}
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnPickerOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleColumnPickerConfirm}
              disabled={selectedExportColumns.size === 0}
            >
              Export CSV
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

ExportTable.displayName = "ExportTable";

// Export types for use in other components
export type { ColumnConfig, ExportColumnOption, ExportTableProps, Row } from "./types";
export { exportToCSV } from "./utils";
